import React, { useEffect, useRef, useState, useCallback } from "react";
import "./HeroCarousel.css";

import img1 from "../../Assets/Images/showcase/Rectangle 6341.png";
import img2 from "../../Assets/Images/showcase/Rectangle 6349.png";
import img3 from "../../Assets/Images/showcase/Rectangle 6350.png";
import img4 from "../../Assets/Images/showcase/Rectangle 6351.png";
import img5 from "../../Assets/Images/showcase/Rectangle 6352.webp";
import img6 from "../../Assets/Images/showcase/Rectangle 6353.webp";
import img7 from "../../Assets/Images/showcase/Rectangle 6354.webp";
import img8 from "../../Assets/Images/showcase/Rectangle 6355.webp";
import img9 from "../../Assets/Images/showcase/Rectangle 6356.webp";
import img10 from "../../Assets/Images/showcase/Rectangle 6357.webp";
import img11 from "../../Assets/Images/showcase/Rectangle 6358.webp";
import img12  from "../../Assets/Images/showcase/Rectangle 6359.webp";


const SLIDES = [img1, img2, img3, img4, img5, img6, img7, img8, img9, img10, img11, img12];
const AUTOPLAY_MS = 3500;
const TRANSITION_MS = 600;
const REPEAT = 12; // how many times SLIDES is repeated in the track

export default function HeroCarousel() {
  const n = SLIDES.length;

  // Repeat SLIDES REPEAT times: bigArray[i] === bigArray[i ± n] always,
  // so any reset of (activeIndex ± n) is content-identical.
  const bigArray = Array.from({ length: REPEAT * n }, (_, i) => SLIDES[i % n]);

  const START_INDEX = Math.floor(REPEAT / 2) * n; // start in the middle repetition
  const RESET_AT = bigArray.length - 2; // upper bound: last index with a right-peek neighbor
  const RESET_AT_LOW = 1;               // lower bound: first index with a left-peek neighbor

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(START_INDEX);
  const [noTransition, setNoTransition] = useState(false);
  const autoplayRef = useRef(null);

  /* Measure container width responsively */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const goTo = useCallback((dir) => {
    setActiveIndex((prev) => prev + dir);
  }, []);

  /* Autoplay */
  useEffect(() => {
    autoplayRef.current = setInterval(() => goTo(1), AUTOPLAY_MS);
    return () => clearInterval(autoplayRef.current);
  }, [goTo]);

  /* Infinite loop reset: triggered precisely when the slide
     transform transition finishes. bigArray[i] === bigArray[i ± n],
     so shifting by n is always content-identical. Both directions
     are handled — autoplay drifts upward, manual clicks can drift
     either way.

     IMPORTANT: .hero-carousel-slide also animates its own `transform`
     (scale on active/inactive), and transitionend bubbles. Without
     the e.target check below, the track would receive multiple
     "transform" transitionend events per index change (one from
     itself + one per child slide finishing its scale), causing the
     reset to fire 2-3x in a row and stack up — which both skips
     repeats unpredictably AND can push activeIndex out of bigArray's
     bounds, producing a NaN transform that visually freezes the
     carousel entirely. */
  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return; // ignore bubbled events from child slides
    if (e.propertyName !== "transform") return;

    if (activeIndex >= RESET_AT) {
      setNoTransition(true);
      setActiveIndex((prev) => prev - n);
    } else if (activeIndex <= RESET_AT_LOW) {
      setNoTransition(true);
      setActiveIndex((prev) => prev + n);
    }
  };

  /* Re-enable transition right after the instant jump */
  useEffect(() => {
    if (noTransition) {
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [noTransition]);

  /* Self-healing guard: if activeIndex ever drifts out of bigArray's
     valid range (shouldn't happen now that handleTransitionEnd is
     fixed, but cheap insurance against any future edge case), snap
     back to a safe middle index instantly instead of freezing on a
     NaN transform. */
  useEffect(() => {
    if (activeIndex < 0 || activeIndex >= bigArray.length) {
      setNoTransition(true);
      setActiveIndex(START_INDEX);
    }
  }, [activeIndex, bigArray.length, START_INDEX]);

/* ── Layout math ── */
  const isMobile = containerWidth > 0 && containerWidth < 768;
  const gap = isMobile ? 0 : 24;
  const activeWidth = containerWidth * (isMobile ? 1 : 0.68);
  const sideWidth = isMobile
    ? 0
    : Math.max(0, (containerWidth - activeWidth) / 2 - gap);

const activeHeight = isMobile
  ? 250
  : activeWidth * 0.56;

const sideHeight = isMobile
  ? 250
  : activeHeight * 0.78;

  let offset = 0;
  const offsets = [];
  const widths = [];
  const heights = [];
  bigArray.forEach((_, i) => {
    const isActive = i === activeIndex;
    const w = isActive ? activeWidth : sideWidth;
    widths.push(w);
    heights.push(isActive ? activeHeight : sideHeight);
    offsets.push(offset);
    offset += w + gap;
  });

  const translateX =
    containerWidth > 0 && offsets[activeIndex] !== undefined
      ? containerWidth / 2 - (offsets[activeIndex] + widths[activeIndex] / 2)
      : 0;

  const handleSlideClick = (i) => {
    if (i === activeIndex) return;
    setActiveIndex(i);
  };

  return (
    <div
      className="hero-carousel"
      ref={containerRef}
      style={{
  height:
    containerWidth > 0
      ? activeHeight + 50 // extra space for dots
      : undefined,
}}
    >
      <div
        className="hero-carousel-track"
        onTransitionEnd={handleTransitionEnd}
        style={{
          gap: `${gap}px`,
          transform: `translateX(${translateX}px)`,
          transition: noTransition ? "none" : `transform ${TRANSITION_MS}ms ease`,
        }}
      >
        {bigArray.map((src, i) => (
          <div
            key={i}
            className={`hero-carousel-slide ${
              i === activeIndex ? "hero-carousel-slide--active" : ""
            }`}
            style={{ width: widths[i], height: heights[i] }}
            onClick={() => handleSlideClick(i)}
          >
            <img src={src} alt="" />
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="hero-carousel-dots">
        {SLIDES.map((_, i) => {
          const isActive = activeIndex % n === i;
          const handleDotClick = () => {
            const base = Math.floor(activeIndex / n) * n;
            setActiveIndex(base + i);
          };
          return (
            <button
              key={i}
              className={`hero-carousel-dot ${
                isActive ? "hero-carousel-dot--active" : ""
              }`}
              onClick={handleDotClick}
              aria-label={`Go to slide ${i + 1}`}
            />
          );
        })}
      </div>
    </div>
  );
}